'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@govinity/ui/components/ui/button';
import { Input } from '@govinity/ui/components/ui/input';

interface PortalHeroSearchProps {
  governmentCode: string;
}

/**
 * Prominent, always-visible search input shown in a band at the top of the
 * portal hero banner. The input is displayed without any hover/click-to-expand
 * interaction; submitting (Enter or the Search button) redirects to the full
 * search page with the query pre-filled.
 */
export function PortalHeroSearch({ governmentCode }: PortalHeroSearchProps) {
  const t = useTranslations('PortalHeroSearch');
  const router = useRouter();
  const [inputValue, setInputValue] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = inputValue.trim();
    if (!q) return;
    router.push(`/portal/${governmentCode}/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <form onSubmit={handleSubmit} role="search" className="flex w-full items-center gap-2">
      <div className="relative flex-1 sm:flex-none">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchInputAriaLabel')}
          className="bg-background h-8 w-full overflow-hidden pl-10 text-ellipsis shadow-sm transition-[width] duration-200 ease-out sm:w-80 sm:focus:w-md"
        />
      </div>
      <Button
        type="submit"
        size="sm"
        className="h-8 shrink-0"
        disabled={!inputValue.trim()}
        aria-label={t('searchButtonAriaLabel')}
      >
        <Search />
        <span className="hidden sm:inline">{t('searchButtonLabel')}</span>
      </Button>
    </form>
  );
}
